// src/pages/admin/DemoLeads.jsx
// Shows all demo survey responses with CSV export — drop into SystemAdmin tabs
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Badge } from '../../components/ui';
import { Download, RefreshCw, Search, Users } from 'lucide-react';

export default function DemoLeads() {
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('demo_leads')
      .select('*')
      .order('created_at', { ascending: false });
    setLeads(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    return !q || l.email?.toLowerCase().includes(q) || l.name?.toLowerCase().includes(q) || l.company?.toLowerCase().includes(q);
  });

  // Stats
  const today = new Date().toISOString().split('T')[0];
  const todayCount = leads.filter(l => l.created_at?.startsWith(today)).length;
  const thisWeek   = leads.filter(l => {
    const d = new Date(l.created_at);
    return (Date.now() - d.getTime()) < 7 * 86400000;
  }).length;
  const topUseCase = Object.entries(
    leads.reduce((acc, l) => { if (l.use_case) acc[l.use_case] = (acc[l.use_case] || 0) + 1; return acc; }, {})
  ).sort((a, b) => b[1] - a[1])[0];

  const exportCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Company', 'Role', 'Use Case', 'Team Size'];
    const rows = leads.map(l => [
      l.created_at ? new Date(l.created_at).toLocaleDateString() : '',
      `"${(l.name || '').replace(/"/g, '""')}"`,
      `"${(l.email || '').replace(/"/g, '""')}"`,
      `"${(l.company || '').replace(/"/g, '""')}"`,
      `"${(l.role || '').replace(/"/g, '""')}"`,
      `"${(l.use_case || '').replace(/"/g, '""')}"`,
      l.team_size || '',
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const a   = Object.assign(document.createElement('a'), {
      href:     URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `revhapi-demo-leads-${today}.csv`,
    });
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total leads',    value: leads.length },
          { label: 'Today',          value: todayCount },
          { label: 'This week',      value: thisWeek },
          { label: 'Top use case',   value: topUseCase ? `${topUseCase[0]} (${topUseCase[1]})` : '—' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="text-xl font-bold mt-1 truncate">{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Demo survey responses</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-48" />
              </div>
              <Button variant="outline" onClick={load} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button onClick={exportCSV}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {['Date','Name','Email','Company','Role','Use case','Team'].map(h => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-muted-foreground text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center">
                    <Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-muted-foreground text-sm">No demo leads yet</p>
                  </td></tr>
                ) : filtered.map(l => (
                  <tr key={l.id} className="hover:bg-accent/50 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">{l.name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-blue-600">{l.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{l.company || '—'}</td>
                    <td className="px-4 py-3">
                      {l.role ? <Badge variant="outline" className="text-xs">{l.role}</Badge> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{l.use_case || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{l.team_size || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
